import React, { useEffect, useState } from 'react';

import { Box } from '@mui/material';

import { CommonComponentProps, ImageObject } from '../../pages/types';
import ImageFullscreen from './image-fullscreen';

interface ImageGalleryProps extends CommonComponentProps {
  images: ImageObject[];
  rowHeight: string | number;
  columns?: number; // Number of columns on desktop
  minColumns?: number; // Minimum columns on mobile (overrides responsive behavior)
  gap?: string; // Gap between images
}

const ImageGallery: React.FC<ImageGalleryProps> = ({
  id,
  images,
  rowHeight,
  columns = 3, // Default to 3 columns
  minColumns, // No default - optional override
  gap = '8px', // Default gap
  sx,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());

  // Calculate responsive columns
  const tabletColumns = Math.max(1, Math.floor(columns / 2)); // Half of desktop columns
  const mobileColumns = minColumns || 1; // Use minColumns if specified, otherwise 1

  // Preload images in order to prevent flickering
  useEffect(() => {
    // Reset loaded images when images prop changes
    setLoadedImages(new Set());

    // Preload images sequentially
    const preloadImages = async () => {
      for (let i = 0; i < images.length; i++) {
        const img = new Image();
        img.src = images[i].url || '';

        try {
          await new Promise<void>((resolve, reject) => {
            img.onload = () => {
              setLoadedImages((prev) => {
                const newSet = new Set(prev);
                newSet.add(i);
                return newSet;
              });
              resolve();
            };
            img.onerror = () => {
              // Even on error, mark as "loaded" so it doesn't block others
              setLoadedImages((prev) => {
                const newSet = new Set(prev);
                newSet.add(i);
                return newSet;
              });
              resolve();
            };
            // Timeout after 5 seconds to prevent blocking
            setTimeout(() => {
              setLoadedImages((prev) => {
                const newSet = new Set(prev);
                newSet.add(i);
                return newSet;
              });
              resolve();
            }, 5000);
          });
        } catch (error) {
          console.error(`Failed to load image ${i}:`, error);
        }
      }
    };

    preloadImages();
  }, [images]);

  return (
    <Box
      id={id}
      sx={{
        py: '10px',
        display: 'grid',
        gridTemplateColumns: {
          xs: `repeat(${mobileColumns}, 1fr)`, // 1 column on mobile
          sm: `repeat(${tabletColumns}, 1fr)`, // Half columns on tablet
          md: `repeat(${columns}, 1fr)`, // Full columns on desktop
        },
        gridAutoRows: rowHeight, // Set the height of the rows
        gap: gap, // Use configurable gap
        ...sx,
      }}
    >
      {images.map((image, index) => (
        <ImageFullscreen
          firstImage={image}
          images={images}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
          key={index}
        >
          <Box
            component="img"
            src={image.url || ''}
            alt={image.altText || `Image ${index + 1}`}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              aspectRatio: '9/9',
              cursor: 'pointer',
              opacity: loadedImages.has(index) ? 1 : 0,
              transition: 'opacity 0.3s ease-in',
            }}
            onClick={() => setCurrentIndex(index)}
          />
        </ImageFullscreen>
      ))}
    </Box>
  );
};

export default ImageGallery;
